import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SectionHeading from "../components/ui/SectionHeading.jsx";
import CTABanner from "../components/ui/CTABanner.jsx";
import Marquee from "../components/ui/Marquee.jsx";
import FacilityCard from "../components/cards/FacilityCard.jsx";
import { Reveal, ImageReveal } from "../components/motion/Reveal.jsx";
import useSeo from "../hooks/useSeo.js";
import { FACILITIES } from "../content.js";

const FILTERS = ["All", "Performance", "Sports", "Family", "Lifestyle"];

export default function FacilitiesPage() {
  const [filter, setFilter] = useState("All");
  const [highlight, setHighlight] = useState(null);

  useEffect(() => {
    const onHighlight = (e) => {
      const anchor = e.detail?.anchor || "";
      const id = anchor.replace("facility-", "");
      if (id) {
        setHighlight(id);
        setTimeout(() => setHighlight((h) => (h === id ? null : h)), 5000);
      }
    };
    window.addEventListener("oneiros-highlight", onHighlight);
    return () => window.removeEventListener("oneiros-highlight", onHighlight);
  }, []);

  useSeo({
    title: "Facilities | Gym, Swimming, Badminton, Tennis & More — Oneiros Sports Club Surat",
    description:
      "16 world-class facilities under one roof at Oneiros Sports Club Surat — gym, swimming, fitness studio, lawn tennis, badminton, pickleball, karate, skating, kids arenas, recovery, cafe and banquet.",
    image: "/oneiros/fac-gym.jpg",
  });
  const shown = filter === "All" ? FACILITIES : FACILITIES.filter((f) => f.category === filter);
  return (
    <>
      <section className="mx-auto max-w-[1440px] px-6 pb-12 pt-36 lg:px-12 lg:pt-48">
        <SectionHeading
          eyebrow="Facilities"
          lines={["Everything You Need.", "Under One Roof."]}
          copy="From high-performance training to leisure, recovery and family experiences, Oneiros brings multiple worlds together under one roof."
        />
        <Reveal delay={0.2}>
          <div className="no-scrollbar mt-10 flex gap-2 overflow-x-auto pb-1" data-testid="facilities-filter">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`facilities-filter-${f.toLowerCase()}`}
                onClick={() => setFilter(f)}
                className={`shrink-0 rounded-full border px-6 py-3 text-[11px] font-bold uppercase tracking-[0.18em] transition-colors duration-300 ${
                  filter === f ? "border-brand bg-brand text-white" : "border-hairline text-mute hover:border-navy hover:text-navy"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 pb-24 lg:px-12">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 md:gap-6">
          {shown.map((f) => (
            <FacilityCard
              key={f.id}
              facility={f}
              index={FACILITIES.indexOf(f)}
              highlighted={highlight === f.id}
            />
          ))}
        </div>
      </section>

      {/* BANQUET & CELEBRATIONS */}
      <section data-testid="banquet-section" className="border-t border-hairline">
        <div className="mx-auto grid max-w-[1440px] items-center gap-0 lg:grid-cols-2">
          <div className="px-6 py-20 lg:px-12 lg:py-24">
            <SectionHeading
              eyebrow="Celebrations"
              lines={["Banquets &", "Celebrations."]}
              copy="Weddings, receptions and milestones at Oneiros — celebrate life's biggest moments in elegant spaces, then enquire to book your date."
            />
            <Reveal delay={0.2} className="mt-8">
              <Link to="/contact" className="group inline-flex items-center gap-3 rounded-full bg-brand px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300">
                Book a Celebration
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <ImageReveal
            src="/oneiros/banquet-wide.jpg"
            alt="Oneiros banquet and celebration venues in Surat"
            className="aspect-[16/10] w-full lg:aspect-auto lg:min-h-[480px]"
          />
        </div>
      </section>

      <section className="grain overflow-hidden bg-navy-obsidian py-20 text-white lg:py-24">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-center gap-x-8 gap-y-4 px-6 text-center lg:px-12">
          {["Train.", "Play.", "Recover.", "Repeat."].map((w, i) => (
            <Reveal key={w} delay={i * 0.08}>
              <span className="flex items-center gap-8 font-display text-3xl sm:text-4xl lg:text-5xl">
                {w}
                {i < 3 && <span className="hidden h-2 w-2 rounded-full bg-brand sm:inline-block" />}
              </span>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <SectionHeading eyebrow="The Standard" lines={["Performance.", "Sports.", "Wellness.", "Community."]} />
        <div className="mt-12 grid gap-6 md:grid-cols-4">
          {[
            ["Performance", "Strength, conditioning and elite training zones built for those who train to dominate."],
            ["Sports", "Professional courts, pools and arenas for skill, agility and competitive play."],
            ["Wellness", "Recovery, relaxation and everyday health for every generation."],
            ["Community", "Shared spaces where families, athletes and friends move together."],
          ].map(([t, c], i) => (
            <Reveal key={t} delay={i * 0.08}>
              <div className="flex h-full flex-col gap-3 border-t-2 border-brand/70 bg-mist p-8">
                <p className="font-display text-3xl text-navy">{t}</p>
                <p className="text-sm leading-relaxed text-mute">{c}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee items={["Gym", "Swimming", "Badminton", "Lawn Tennis", "Pickleball", "Kids Arena", "Recovery", "Cafe", "Banquet"]} slow />
      <div className="h-px w-full bg-navy-obsidian" />
      <CTABanner
        eyebrow="Facilities"
        lines={["Find Your Space", "At Oneiros."]}
        primary={{ label: "Enquire Now", to: "/contact" }}
        secondary={{ label: "Explore Our Clubs", to: "/our-clubs" }}
        testId="facilities-final-cta"
      />
    </>
  );
}
